import { Component, OnInit, computed, signal } from '@angular/core';
import { ApiTranslationsExportService, FileFormat } from '../../../../shared/services/api-translations-export.service';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { Router } from '@angular/router';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiTranslationsHistoryService, TranslationsHistoryItem } from '../../../../shared/services/api-translationsHistory.service';
import { ApiSurvey } from '../../../../shared/services/api-organization.service';

@Component({
  selector: 'app-translations-export',
  templateUrl: './translations-export.component.html',
  styleUrls: ['./translations-export.component.css'],
  imports: [CommonModule, FormsModule, PageLoaderComponent, BreadcrumbComponent]
})
export class TranslationsExportComponent implements OnInit {
  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  requesting = signal<boolean>(false);
  loadingHistory = signal<boolean>(false);
  surveyList: ApiSurvey[];
  organizationId: string = '';
  selectedSurveyId: string = '';
  historyFilterSurveyId: string = '';
  copiedText = signal<string | null>(null);
  private allHistoryItems: TranslationsHistoryItem[] = [];
  historyItems = signal<TranslationsHistoryItem[]>([]);
  totalCount = signal<number>(0);
  currentPage = signal<number>(1);
  historyPerPage: number = 10;
  readonly perPageOptions = [5, 10, 25, 50];

  get totalPages(): number {
    return Math.ceil(this.totalCount() / this.historyPerPage) || 1;
  }

  readonly canEdit = computed(() => this.userStore.isAdmin());

  constructor(
    private router: Router,
    public organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private translationsHistoryService: ApiTranslationsHistoryService,
    private translationsExportService: ApiTranslationsExportService) {
      this.surveyList = organizationStore.selectedOrganizationSurveys();
    }

  ngOnInit() {
    this.organizationId = this.organizationStore.selectedOrganizationId() ?? '';
    if (!this.organizationId) {
      this.router.navigate(['/organizations']);
      return;
    }
    this.loadAllHistory();
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedText.set(text);
      setTimeout(() => this.copiedText.set(null), 1500);
    });
  }

  getSurveyName(surveyId: string): string {
    return this.surveyList.find(s => s.id === surveyId)?.name ?? surveyId;
  }

  private applyFilterAndPage(): void {
    const filtered = this.historyFilterSurveyId
      ? this.allHistoryItems.filter(i => i.surveyId === this.historyFilterSurveyId)
      : this.allHistoryItems;
    this.totalCount.set(filtered.length);
    const start = (this.currentPage() - 1) * this.historyPerPage;
    this.historyItems.set(filtered.slice(start, start + this.historyPerPage));
  }

  loadAllHistory(): void {
    this.loadingHistory.set(true);
    this.translationsHistoryService.getTranslationsHistory(this.organizationId, null, 'Export', 1, 500).subscribe({
      next: (history) => {
        this.allHistoryItems = history.items;
        this.currentPage.set(1);
        this.applyFilterAndPage();
        this.loadingHistory.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load export history. | ' + err);
        this.loadingHistory.set(false);
      }
    });
  }

  onHistoryFilterChange(): void {
    this.currentPage.set(1);
    this.applyFilterAndPage();
  }

  onPerPageChange(): void {
    this.currentPage.set(1);
    this.applyFilterAndPage();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.applyFilterAndPage();
  }

  requestExport(format: FileFormat): void {
    if (!this.canEdit() || !this.selectedSurveyId) return;

    this.requesting.set(true);
    this.translationsExportService.requestTranslationsExport(this.organizationId, this.selectedSurveyId, format).subscribe({
      next: () => {
        this.requesting.set(false);
        this.loadAllHistory();
      },
      error: (err) => {
        this.error.set('Request failed for translations export. | ' + err);
        this.requesting.set(false);
      }
    });
  }
}
